import React, { useMemo } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  UserCheck,
  Building,
  Clock,
  Activity,
  AlertTriangle,
  Heart,
  FileText,
  CreditCard,
} from "lucide-react";
import { OverviewTabProps } from "../../../../../interfaces/screens/content/patientDetails";

// Move static card configurations outside component
const statusCards = [
  {
    title: "Current Status",
    icon: Activity,
    value: "Stable",
    description: "Tolerating clear fluids well",
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-white dark:bg-darkCardBackground",
    borderColor: "border-gray-100 dark:border-gray-700",
  },
  {
    title: "Lab Alert",
    icon: AlertTriangle,
    value: "WCC ↑",
    description: "11.2 × 10⁹/L",
    color: "text-orange-600 dark:text-orange-400",
    bgColor: "bg-orange-50 dark:bg-orange-900/30",
    borderColor: "border-orange-200 dark:border-orange-800",
  },
  {
    title: "Pain Level",
    icon: Heart,
    value: "5/10",
    description: "At rest",
    color: "text-orange-600 dark:text-orange-400",
    bgColor: "bg-white dark:bg-darkCardBackground",
    borderColor: "border-gray-100 dark:border-gray-700",
  },
  {
    title: "Next Review",
    icon: Clock,
    value: "2h",
    description: "Pain assessment",
    color: "text-[#2823F5] dark:text-[#4F4DFF]",
    bgColor: "bg-white dark:bg-darkCardBackground",
    borderColor: "border-gray-100 dark:border-gray-700",
  },
];

export const OverviewTab: React.FC<OverviewTabProps> = React.memo(
  ({ patient }) => {
    // Memoize the last medical report calculation
    const lastMedicalReport = useMemo(
      () =>
        patient.patient_forms
          ?.filter((form: any) => form.form_type === "medical")
          .sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          )[0],
      [patient.patient_forms],
    );

    // Memoize the getSectionName function
    const getSectionName = useMemo(
      () => (fields: any[]) => {
        const sections = fields
          .map((field) => field.form_field?.section)
          .filter(Boolean);
        if (sections.length > 0) {
          return sections[0];
        }
        return "Additional Information";
      },
      [],
    );

    // Memoize the renderFormFields function
    const renderFormFields = useMemo(
      () => (fields: any[]) => {
        const validFields = fields
          .filter(
            (field: any) =>
              field.value !== null &&
              field.value !== undefined &&
              field.value !== "",
          )
          .sort(
            (a: any, b: any) =>
              (a.form_field?.order || 0) - (b.form_field?.order || 0),
          );

        const chunks = [];
        for (let i = 0; i < validFields.length; i += 4) {
          chunks.push(validFields.slice(i, i + 4));
        }

        return chunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6"
          >
            <div className="flex items-center mb-4">
              <FileText className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-medium dark:text-white">
                {chunkIndex === 0
                  ? "Last Medical Report"
                  : getSectionName(chunk)}
              </h3>
            </div>
            <div className="space-y-4">
              {chunkIndex === 0 && (
                <>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Report Type
                    </p>
                    <p className="text-sm dark:text-white">
                      {lastMedicalReport.form_label}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Date
                    </p>
                    <p className="text-sm dark:text-white">
                      {new Date(
                        lastMedicalReport.created_at,
                      ).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </>
              )}
              <div className="grid grid-cols-1 gap-3">
                {chunk.map((field: any) => (
                  <div key={field.id}>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {field.label}
                    </p>
                    <p className="text-sm dark:text-white mt-1">
                      {field.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ));
      },
      [lastMedicalReport, getSectionName],
    );

    return (
      <div className="space-y-6">
        {/* Current Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statusCards.map((card, index) => (
            <div
              key={index}
              className={`rounded-lg border ${card.borderColor} ${card.bgColor} p-4`}
            >
              <div className="flex items-center justify-between pb-2">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {card.title}
                </h3>
                <card.icon className="h-4 w-4 text-gray-400" />
              </div>
              <div className={`text-2xl font-medium ${card.color}`}>
                {card.value}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {card.description}
              </p>
            </div>
          ))}
        </div>

        {/* Patient Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
            <div className="flex items-center mb-4">
              <User className="w-5 h-5 mr-2 text-[#2823F5] dark:text-[#4F4DFF]" />
              <h3 className="text-lg font-medium dark:text-white">
                Personal Information
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                  Age
                </p>
                <p className="text-sm dark:text-white mt-1">
                  {patient.age} years
                </p>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                  Gender
                </p>
                <p className="text-sm dark:text-white mt-1">{patient.gender}</p>
              </div>
              {patient.nationality && (
                <div>
                  <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                    Nationality
                  </p>
                  <p className="text-sm dark:text-white mt-1">
                    {patient.nationality}
                  </p>
                </div>
              )}
              {patient.primary_language && (
                <div>
                  <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                    Language
                  </p>
                  <p className="text-sm dark:text-white mt-1">
                    {patient.primary_language}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
            <div className="flex items-center mb-4">
              <Phone className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
              <h3 className="text-lg font-medium dark:text-white">
                Contact Information
              </h3>
            </div>
            <div className="space-y-3">
              {patient.mobile_number && (
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm dark:text-white">
                    {patient.mobile_number}
                  </span>
                </div>
              )}
              {patient.email && (
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm dark:text-white">
                    {patient.email}
                  </span>
                </div>
              )}
              {(patient.address ||
                patient.area ||
                patient.city ||
                patient.governorate) && (
                <div className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div className="text-sm dark:text-white">
                    <p className="font-bold">Address</p>
                    <p>Street Name, Building Name</p>
                    <p>Saida Area, Saida City, South Lebanon Governorate</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Insurance & Financials */}
          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
            <div className="flex items-center mb-4">
              <CreditCard className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
              <h3 className="text-lg font-medium dark:text-white">
                Insurance & Financials
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <p className="font-bold text-gray-500 dark:text-gray-400">
                Primary Insurance:
              </p>
              <p className="dark:text-white text-right">NSSF</p>

              <p className="font-bold text-gray-500 dark:text-gray-400">
                NSSF Number:
              </p>
              <p className="dark:text-white text-right">123456/1</p>

              <p className="font-bold text-gray-500 dark:text-gray-400">
                Financial Class:
              </p>
              <p className="dark:text-white text-right">Insured</p>

              <p className="font-bold text-gray-500 dark:text-gray-400">
                Secondary Insurance:
              </p>
              <p className="dark:text-white text-right">None</p>
            </div>
          </div>

          {/* Last Medical Report */}
          {lastMedicalReport ? (
            renderFormFields(lastMedicalReport.form_fields)
          ) : (
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
              <div className="flex items-center mb-4">
                <FileText className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-medium dark:text-white">
                  Last Medical Report
                </h3>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No Medical Case Assessment on file
              </p>
            </div>
          )}

          {/* Emergency Contact */}
          {(patient.emergency_contact_name ||
            patient.relationship ||
            patient.emergency_contact_phone) && (
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
              <div className="flex items-center mb-4">
                <UserCheck className="w-5 h-5 mr-2 text-orange-600 dark:text-orange-400" />
                <h3 className="text-lg font-medium dark:text-white">
                  Emergency Contact
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                  Name
                </p>
                <p className="text-sm dark:text-white">Jane Doe</p>

                <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                  Relationship
                </p>
                <p className="text-sm dark:text-white">Spouse</p>
                {patient.emergency_contact_phone && (
                  <>
                    <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                      Phone
                    </p>
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-sm dark:text-white">
                        {patient.emergency_contact_phone}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Admission Details */}
          {(patient.registration_date || patient.employer) && (
            <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
              <div className="flex items-center mb-4">
                <Building className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-lg font-medium dark:text-white">
                  Admission Details
                </h3>
              </div>
              <div className="space-y-3">
                {patient.registration_date && (
                  <div>
                    <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                      Registration Date
                    </p>
                    <p className="text-sm dark:text-white mt-1">
                      {new Date(patient.registration_date).toLocaleDateString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        },
                      )}
                    </p>
                  </div>
                )}
                {patient.employer && (
                  <div>
                    <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                      Employer
                    </p>
                    <p className="text-sm dark:text-white mt-1">
                      {patient.employer}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
          <div className="flex items-center mb-4">
            <Clock className="w-5 h-5 mr-2" />
            <h3 className="text-lg font-medium dark:text-white">
              Recent Activity
            </h3>
          </div>
          <div className="space-y-4">
            {patient.recentActivity && patient.recentActivity.length > 0 ? (
              patient.recentActivity.map((activity: any, index: number) => (
                <div key={index} className="flex items-center space-x-3">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      activity.type === "appointment"
                        ? "bg-primary"
                        : activity.type === "medication"
                          ? "bg-green-500"
                          : activity.type === "lab_result"
                            ? "bg-blue-500"
                            : "bg-gray-500"
                    }`}
                  />
                  <div className="flex-1">
                    <p className="text-sm dark:text-white">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {activity.timestamp}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No recent activity
              </p>
            )}
          </div>
        </div>
      </div>
    );
  },
);

OverviewTab.displayName = "OverviewTab";
